import { useEffect, useRef } from "react";

import { clamp } from "@perceptron/utils/math";

import type { TimelinePlayerBridge } from "../bridge";

// The timeline ruler (`data-timeline-ruler`) is a slider that handles its own
// arrows, Page Up/Down and Home/End, so every other player key (Space, frames,
// zoom) still works while it has focus. Other sliders, such as volume, keep
// their native keys.
const SLIDER_SELECTOR = '[role="slider"]:not([data-timeline-ruler])';

// When focus is on one of these, the focused element's own keyboard semantics
// take priority — typing into an input, activating a button with Space,
// moving the selection in a tablist with arrows, etc. Document-scoped player
// shortcuts must skip these or they'll collide with normal keyboard UX.
const FOCUS_BLOCKS_SHORTCUT_SELECTOR = [
  // Editable text input
  "input",
  "textarea",
  "select",
  "[contenteditable]",
  '[role^="menuitem"]',
  // Keyboard-activated interactive controls
  "button",
  "a[href]",
  "summary",
  '[role="button"]',
  '[role="link"]',
  '[role^="tab"]',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="option"]',
  '[role^="combobox"]',
  SLIDER_SELECTOR,
].join(", ");

/** ← / → and J / L, and the arrow keys on the focused timeline ruler. */
export const SEEK_STEP_SECONDS = 5;
// These widgets own character keys for typing/typeahead or Home/End for
// navigation, even when their DOM target is a button or nested label.
const TEXT_ENTRY_SELECTOR = [
  "input",
  "textarea",
  "select",
  "[contenteditable]:not([contenteditable='false'])",
  '[role="textbox"]',
  '[role="searchbox"]',
  SLIDER_SELECTOR,
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menu"]',
  '[role="menubar"]',
  '[role^="menuitem"]',
  '[role="tree"]',
  '[role="treeitem"]',
].join(", ");

/** Rows of the shortcut guide, named by their `strings.videoShortcuts` label. */
export type ShortcutGuideLabel =
  | "playback"
  | "seek"
  | "frame"
  | "segment"
  | "boundary"
  | "mute"
  | "fullscreen"
  | "zoom"
  | "fit"
  | "guide";

export type PlayerShortcut = {
  /**
   * Keys that activate this shortcut. Each entry is matched against
   * `event.code` (layout-independent — e.g. "Space", "KeyK") AND `event.key`
   * (layout-aware — e.g. "k", "K"). A match on either fires the action.
   * List both cases of a letter so Caps Lock doesn't disable it.
   */
  keys: string[];
  action: (bridge: TimelinePlayerBridge) => void;
  /**
   * Defaults to `true`: skip when the focused element claims keyboard input —
   * editable text (`<input>`, `<textarea>`, `[contenteditable]`) or interactive
   * controls (`<button>`, `<a>`, `[role="tab"]`, `[role="radio"]`, etc.).
   * `"editable"` permits ordinary buttons but protects text entry, sliders, and typeahead widgets.
   * Override with `false` for shortcuts that should fire regardless of focus.
   */
  respectFocus?: boolean | "editable";
  /** Permit Shift for shortcuts whose actual character requires it, such as < and >. */
  allowShift?: boolean;
  /**
   * How the `?` guide lists this shortcut. Consecutive shortcuts with the same
   * label share a row; `keys` are captions, one per alternative ("Space" is localized).
   */
  guide?: { label: ShortcutGuideLabel; keys: string[] };
};

const seekBy = (bridge: TimelinePlayerBridge, seconds: number) => {
  const duration = bridge.getDuration?.();
  const end = duration !== undefined && Number.isFinite(duration) ? duration : Infinity;
  bridge.seek(clamp(bridge.getCurrentTime() + seconds, 0, end));
};

export const DEFAULT_SHORTCUTS: readonly PlayerShortcut[] = [
  {
    keys: ["Space", "k", "K"],
    action: (bridge) => (bridge.isPaused() ? bridge.play() : bridge.pause()),
    guide: { label: "playback", keys: ["Space", "K"] },
  },
  {
    keys: ["ArrowLeft", "j", "J"],
    action: (bridge) => seekBy(bridge, -SEEK_STEP_SECONDS),
    guide: { label: "seek", keys: ["←", "J"] },
  },
  {
    keys: ["ArrowRight", "l", "L"],
    action: (bridge) => seekBy(bridge, SEEK_STEP_SECONDS),
    guide: { label: "seek", keys: ["→", "L"] },
  },
  {
    keys: [",", "<"],
    action: (bridge) => bridge.stepFrame?.(-1),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "frame", keys: [",", "<"] },
  },
  {
    keys: [".", ">"],
    action: (bridge) => bridge.stepFrame?.(1),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "frame", keys: [".", ">"] },
  },
  {
    keys: ["["],
    action: (bridge) => bridge.skipSegment?.(-1),
    respectFocus: "editable",
    guide: { label: "segment", keys: ["["] },
  },
  {
    keys: ["]"],
    action: (bridge) => bridge.skipSegment?.(1),
    respectFocus: "editable",
    guide: { label: "segment", keys: ["]"] },
  },
  {
    keys: ["Home"],
    action: (bridge) => bridge.seekBoundaryFrame?.("first"),
    respectFocus: "editable",
    guide: { label: "boundary", keys: ["Home"] },
  },
  {
    keys: ["End"],
    action: (bridge) => bridge.seekBoundaryFrame?.("last"),
    respectFocus: "editable",
    guide: { label: "boundary", keys: ["End"] },
  },
  {
    keys: ["m", "M"],
    action: (bridge) => bridge.toggleMuted?.(),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "mute", keys: ["M"] },
  },
  {
    keys: ["f", "F"],
    action: (bridge) => bridge.toggleFullscreen?.(),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "fullscreen", keys: ["F"] },
  },
  {
    keys: ["-", "_"],
    action: (bridge) => bridge.zoomTimeline?.("out"),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "zoom", keys: ["−"] },
  },
  {
    keys: ["=", "+"],
    action: (bridge) => bridge.zoomTimeline?.("in"),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "zoom", keys: ["+"] },
  },
  {
    keys: ["z", "Z"],
    action: (bridge) => bridge.zoomTimeline?.("fit"),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "fit", keys: ["Z"] },
  },
  {
    keys: ["?"],
    action: (bridge) => bridge.showShortcuts?.(),
    respectFocus: "editable",
    allowShift: true,
    guide: { label: "guide", keys: ["?"] },
  },
];

// With several players mounted, the one a person last pressed in or focused
// owns the keyboard; otherwise the most recently mounted one does.
const shortcutOwners: symbol[] = [];
const releaseShortcuts = (owner: symbol) => {
  const index = shortcutOwners.indexOf(owner);
  if (index !== -1) shortcutOwners.splice(index, 1);
};
const claimShortcuts = (owner: symbol) => {
  releaseShortcuts(owner);
  shortcutOwners.push(owner);
};

/**
 * Installs document-level keyboard shortcuts that drive the player through the
 * bridge. Pass an empty array to disable all shortcuts; pass a custom list to
 * override the defaults entirely.
 *
 * Default transport shortcuts pass through when the user is interacting with another control
 * — focused inputs, focused buttons/links/tabs/radios, modifier-key combos
 * (Cmd/Ctrl/Alt, and Shift unless explicitly allowed). Frame punctuation can
 * work on focused buttons, but never inside editable fields. Use `respectFocus: false` to fire
 * regardless of focus (e.g. an Esc handler that should always work).
 *
 * `getScope` returns the player's root element: pressing or focusing inside it
 * makes this player the one that answers shortcuts.
 */
export function usePlayerShortcuts(
  bridge: TimelinePlayerBridge,
  shortcuts: readonly PlayerShortcut[] = DEFAULT_SHORTCUTS,
  getScope?: () => Element | null | undefined,
): void {
  const ownerRef = useRef<symbol | null>(null);
  ownerRef.current ??= Symbol("player-shortcuts");

  useEffect(() => {
    const owner = ownerRef.current!;
    claimShortcuts(owner);
    // Resolved per event: the player's root element can mount after this effect.
    const claim = (event: Event) => {
      if (event.target instanceof Node && getScope?.()?.contains(event.target)) claimShortcuts(owner);
    };
    document.addEventListener("pointerdown", claim, { capture: true });
    document.addEventListener("focusin", claim);
    return () => {
      releaseShortcuts(owner);
      document.removeEventListener("pointerdown", claim, { capture: true });
      document.removeEventListener("focusin", claim);
    };
  }, [getScope]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (shortcutOwners.at(-1) !== ownerRef.current) return;
      // Preserve handled events, composition, and OS/browser key combinations.
      if (event.defaultPrevented || event.isComposing || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as Element | null;
      const focusBlocks = !!target?.closest?.(FOCUS_BLOCKS_SHORTCUT_SELECTOR);
      const isEditing = !!target?.closest?.(TEXT_ENTRY_SELECTOR);
      const match = shortcuts.find((shortcut) => {
        if (event.shiftKey && !shortcut.allowShift) return false;
        if (shortcut.respectFocus === "editable" ? isEditing : focusBlocks && shortcut.respectFocus !== false) {
          return false;
        }
        return shortcut.keys.some((key) => event.code === key || event.key === key);
      });
      if (!match) return;
      event.preventDefault();
      match.action(bridge);
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [bridge, shortcuts]);
}
